/**
 * @file Apollo helper utilities.
 */

import set from 'lodash/set';
import get from 'lodash/get';
import produce from 'immer';

import { SORT_DIRECTION } from '../constants/sort';

/**
 * Creates a "load more" method.
 *
 * @param {Object} options - The options.
 * @param {Object} options.source - The source object as returned from GQL.
 * @param {String} options.recordsPath - The path inside the query object to look for the records as expected by lodash.get.
 * @param {String} [options.limitVariableLabel] - The name of the "limit" variable to be used on the query.
 * @param {String} [options.offsetVariableLabel] - The name of the "offset" variable to be used on the query.
 * @param {Function} options.fetchMore - Apollo's fetchMore function.
 * @param {number} options.limit The limit.
 * @returns {Function}
 */
const createLoadMoreMethod = ({
  source,
  recordsPath,
  limitVariableLabel = 'limit',
  offsetVariableLabel = 'offset',
  limit: defaultLimit,
  fetchMore,
}) => {
  if (!fetchMore) {
    return undefined;
  }

  return ({
    direction = SORT_DIRECTION.ASC,
    limit = defaultLimit,
    offset,
    updateTotals,
  } = {}) =>
    fetchMore({
      variables: {
        [limitVariableLabel]: limit,
        [offsetVariableLabel]:
          offset ??
          get(source, [recordsPath, 'records'].filter(Boolean).join('.'), [])
            .length,
      },
      updateQuery: (prevResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return prevResult;
        }

        return produce(prevResult, draft => {
          const key = Object.keys(prevResult)[0];
          const draftRecords = get(
            draft,
            [key, recordsPath, 'records'].filter(Boolean).join('.'),
            [],
          );
          const draftTotals = get(
            draft,
            [key, recordsPath, 'total'].filter(Boolean).join('.'),
            undefined,
          );
          const fetchMoreResultRecords = get(
            fetchMoreResult,
            [key, recordsPath, 'records'].filter(Boolean).join('.'),
            [],
          );
          const fetchMoreResultTotals = get(
            fetchMoreResult,
            [key, recordsPath, 'total'].filter(Boolean).join('.'),
            undefined,
          );

          if (direction === SORT_DIRECTION.DESC) {
            draftRecords.unshift(...fetchMoreResultRecords);
          } else {
            draftRecords.push(...fetchMoreResultRecords);
          }

          if (
            updateTotals &&
            typeof draftTotals !== 'undefined' &&
            typeof fetchMoreResultTotals !== 'undefined'
          ) {
            set(
              draft,
              [key, recordsPath, 'total'].filter(Boolean).join('.'),
              fetchMoreResultTotals,
            );
          }
        });
      },
    });
};

export { createLoadMoreMethod };
